/**
 * Password Reset Page
 * Allows users to request a password reset email
 */

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { resetPassword } from '@/services/auth';
import { FirebaseError } from 'firebase/app';

export const PasswordReset = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!email.trim()) {
      setError('Por favor, ingrese su correo electrónico');
      return;
    }

    setLoading(true);
    setError('');

    try {
      await resetPassword(email);
      setSuccess(true);
    } catch (error) {
      console.error('Password reset error:', error);

      if (error instanceof FirebaseError) {
        switch (error.code) {
          case 'auth/user-not-found':
            setError('No se encontró una cuenta con este correo electrónico');
            break;
          case 'auth/invalid-email':
            setError('Correo electrónico inválido');
            break;
          case 'auth/too-many-requests':
            setError('Demasiados intentos. Intente más tarde');
            break;
          default:
            setError(
              'Error al enviar el correo de recuperación. Intente nuevamente'
            );
        }
      } else {
        setError(
          'Error al enviar el correo de recuperación. Intente nuevamente'
        );
      }
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          <div>
            <div className="flex justify-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
                <svg
                  className="w-10 h-10 text-green-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
            </div>
            <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
              Correo Enviado
            </h2>
            <p className="mt-2 text-center text-sm text-gray-600">
              Revise su bandeja de entrada
            </p>
          </div>

          <div className="bg-white rounded-lg shadow-lg p-8">
            <div className="text-center space-y-4">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto">
                <svg
                  className="w-8 h-8 text-blue-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                  />
                </svg>
              </div>

              <h3 className="text-xl font-semibold text-gray-900">
                Instrucciones Enviadas
              </h3>

              <p className="text-gray-600">
                Se ha enviado un correo electrónico a <strong>{email}</strong>{' '}
                con instrucciones para restablecer su contraseña.
              </p>

              <div className="bg-blue-50 border border-blue-200 rounded-md p-4 text-left">
                <h4 className="font-medium text-blue-900 mb-2">
                  Pasos a seguir:
                </h4>
                <ul className="text-sm text-blue-700 space-y-1">
                  <li>• Revise su bandeja de entrada y carpeta de spam</li>
                  <li>• Haga clic en el enlace del correo recibido</li>
                  <li>• Cree una nueva contraseña segura</li>
                  <li>• Inicie sesión con su nueva contraseña</li>
                </ul>
              </div>

              <div className="pt-4 space-y-3">
                <Link
                  to="/login"
                  className="btn-primary w-full flex justify-center py-2 px-4 text-sm font-medium"
                >
                  Volver al Inicio de Sesión
                </Link>

                <button
                  onClick={() => {
                    setSuccess(false);
                    setEmail('');
                  }}
                  className="btn-secondary w-full flex justify-center py-2 px-4 text-sm font-medium"
                >
                  Enviar Otro Correo
                </button>
              </div>

              <p className="text-xs text-gray-500">
                Si no recibe el correo en unos minutos, revise su carpeta de
                spam o contacte al administrador del sistema.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <div className="flex justify-center">
            <div className="w-12 h-12 bg-primary-600 rounded-lg flex items-center justify-center">
              <svg
                className="w-8 h-8 text-white"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z"
                />
              </svg>
            </div>
          </div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            Restablecer Contraseña
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            Ingrese su correo para recibir instrucciones
          </p>
        </div>

        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-md p-3">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          <div>
            <label htmlFor="email" className="form-label">
              Correo Electrónico
            </label>
            <input
              id="email"
              name="email"
              type="email"
              autoComplete="email"
              required
              className="form-input"
              placeholder="su.email@ejemplo.com"
              value={email}
              onChange={e => setEmail(e.target.value)}
            />
            <p className="mt-2 text-sm text-gray-500">
              Ingrese el correo electrónico asociado a su cuenta
            </p>
          </div>

          <div>
            <button
              type="submit"
              disabled={loading}
              className="btn-primary w-full flex justify-center py-3 px-4 text-sm font-medium"
            >
              {loading ? (
                <div className="flex items-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Enviando correo...
                </div>
              ) : (
                'Enviar Instrucciones'
              )}
            </button>
          </div>

          <div className="text-center space-y-2">
            <Link
              to="/login"
              className="text-primary-600 hover:text-primary-500 font-medium text-sm"
            >
              ← Volver al Inicio de Sesión
            </Link>
            <br />
            <span className="text-sm text-gray-600">
              ¿No tiene una cuenta?{' '}
              <Link
                to="/register"
                className="text-primary-600 hover:text-primary-500 font-medium"
              >
                Registrarse
              </Link>
            </span>
          </div>
        </form>
      </div>
    </div>
  );
};
